import React, { useMemo, useState } from 'react'
import PageApp from '../../../Page'
import {
  HomeOutlined,
  ClockCircleFilled,
  MailOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import {
  Table,
  Empty,
  Modal,
  Divider,
  Button,
  message,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Tooltip,
  Form,
  Statistic,
  Skeleton,
  Select,
} from 'antd'
import { getAll, recibosEncabezadoNomina, obtenerTotales, enviarRecibosPorCorreo } from '../../../../apis/EnvioRecibosApi'
import { status_recibo_nomina } from '@app/constants/reciboNomina'
import Currency from '../../../../components/common/Currency';
import AnioPeriodo from '../../../../components/forms/anio_periodo'

const { confirm } = Modal
const { Title, Text } = Typography

const routes = [
  {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <ClockCircleFilled />
        <span>Enviar Recibos por correo</span>
      </>
    ),
  },
]

const EnviarRecibosEmailPage = () => {
    // ====== Estado controlado por <AnioPeriodo /> ======
    const [form] = Form.useForm()
    const [header, setHeader] = useState(null)       // { periodo_id, anio, num_periodo }
    const [periodData, setPeriodData] = useState(null)
    const [status, setStatus] = useState(null)
    const [loading, setLoading] = useState(false);

    // ====== Estado de datos ======
    const [items, setItems] = useState([])           // empleados + estatus envío (tabla)
    const [bitacoraInfo, setBitacoraInfo] = useState(null)
    const [filterStatus, setFilterStatus] = useState(null) // filtro de estatus

    // ====== Resumen mock (sustituye con tu API si aplica) ======
    const [resumen, setResumen] = useState({
        percepcionTotal: 0,
        deduccionTotal: 0,
        totalNeto: 0,
        numEmpleados: 0,
    });
    const [resumen2, setResumen2] = useState({
        empNeto: 0,
        recibosTimbrados: 0,
        timbradosPrueba: 0,
        timbradosError: 0
    });

    const cargarResumen = async (values) => {
        setLoading(true);

        const response = await obtenerTotales(values.periodo_id, values.anio, values.num_periodo);
        if (response.ok) {
        setResumen({
            percepcionTotal: response.payload.percepciones,
            deduccionTotal: response.payload.deducciones,
            totalNeto: response.payload.percepciones - response.payload.deducciones,
            numEmpleados: response.payload.total_empleados,
            
        });
        }
        setLoading(false);
    };

  const currency = (n) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n || 0))

  // ====== Items filtrados ======
  const filteredItems = useMemo(() => {
    if (!filterStatus) return items
    return items.filter(item => item.envio_status === filterStatus)
  }, [items, filterStatus])

  // ====== Tabla ======
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: (a, b) => (a.id ?? 0) - (b.id ?? 0), defaultSortOrder: 'ascend', width: 90 },
    { title: 'Nombre', dataIndex: 'name', key: 'name' },
    {
      title: 'Correo',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    { title: 'Puesto', dataIndex: 'job_title', key: 'job_title', responsive: ['md'] },
    {
      title: 'Tipo de nómina',
      dataIndex: 'periodo',
      key: 'periodo',
      render: (_, obj) => obj?.periodo?.descripcion || <Text type="secondary">—</Text>,
    },
    {
      title: 'Estatus',
      dataIndex: 'envio_status',
      key: 'envio_status',
      align: 'center',
      width: 140,
      sorter: (a, b) => String(a.envio_status ?? '').localeCompare(String(b.envio_status ?? '')),
      defaultSortOrder: 'ascend',
      render: (v, obj) => {
        const statusConfig = {
          [status_recibo_nomina.EN_PROCESO]: { label: 'En proceso', color: 'gold' },
          [status_recibo_nomina.PROCESADO]: { label: 'Procesado', color: 'blue' },
          [status_recibo_nomina.CERRADO]: { label: 'Cerrado', color: 'purple' },
          [status_recibo_nomina.TIMBRADO]: { label: 'Timbrado', color: 'cyan' },
          [status_recibo_nomina.ENVIADO]: { label: 'Enviado', color: 'green' },
          [status_recibo_nomina.ERROR]: { label: 'Error', color: 'red' },
        }

        const cfg = statusConfig[v]
        if (!cfg) return <Tag>—</Tag>

        if (v === status_recibo_nomina.ERROR) {
          return (
            <Tooltip title={obj.envio_error || 'Error desconocido'}>
              <Tag color={cfg.color}>{cfg.label}</Tag>
            </Tooltip>
          )
        }

        return <Tag color={cfg.color}>{cfg.label}</Tag>
      },
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: 140,
      render: (_, object) => (
        <Space>
          {object.envio_status === 'ERROR' && (
            <Button size="small" danger onClick={() => reenviarUno(object)}>
              Reintentar
            </Button>
          )}
        </Space>
      ),
    },
  ]

  // ====== Acciones ======
    const refreshData = async (values) => {
        // Usa SIEMPRE los valores pasados por AnioPeriodo para evitar race conditions
        if (!values?.periodo_id || !values?.anio || !values?.num_periodo) return;

        // 1) Bitácora desde recibos (igual patrón que TimbrarNomina)
        let data = []
        let usuarios_esperados = 0
        let itemsErrorTotal = 0
        let itemsTimbradoTotal = 0
        let itemsEnviadoTotal = 0

        const responseEncabezado = await recibosEncabezadoNomina(values.periodo_id, values.anio, values.num_periodo)
        if (responseEncabezado?.ok && Array.isArray(responseEncabezado.payload)) {
          data = responseEncabezado.payload
            usuarios_esperados = data.length
            for (const item of data) {
                if (item.estatus === status_recibo_nomina.ERROR) itemsErrorTotal++
                if (item.estatus === status_recibo_nomina.TIMBRADO || item.estatus === status_recibo_nomina.ENVIADO) itemsTimbradoTotal++
                if (item.estatus === status_recibo_nomina.ENVIADO) itemsEnviadoTotal++
            }
            setBitacoraInfo({
                usuarios_procesados: itemsTimbradoTotal + itemsErrorTotal,
                usuarios_procesados_exito: itemsTimbradoTotal,
                usuarios_procesados_error: itemsErrorTotal,
                usuarios_enviados: itemsEnviadoTotal,
                usuarios_esperados,
            })
            setResumen2({
                empNeto: 0,
                recibosTimbrados: itemsTimbradoTotal,
                timbradosPrueba: 0,
                timbradosError: itemsErrorTotal
            });
        } else {
        // sin datos o error → bitácora en 0
        setBitacoraInfo({
            usuarios_procesados: 0,
            usuarios_procesados_exito: 0,
            usuarios_procesados_error: 0,
            usuarios_enviados: 0,
            usuarios_esperados: 0,
        })
        }

        // 2) Lista para la tabla (envío de recibos)
        const response = await getAll()
        if (response?.ok && Array.isArray(response.payload)) {
        const rows = response.payload
          .filter((r) => data.some((d) => d.odoo_empleado_id === r.id))
          .map((r) => {
            const encabezado = data.find((d) => d.odoo_empleado_id === r.id)
            return {
              ...r,
              email: r.email,
              envio_status: encabezado?.estatus,
              envio_error: encabezado?.error,
            }
          })

        setItems(rows)
        } else {
        setItems([])
        }
    }

    const reenviarUno = async (row) => {
        message.loading({ key: 're1', content: 'Reenviando…' })
        setTimeout(() => {
        message.success({ key: 're1', content: 'Enviado', duration: 1.2 })
        setItems((prev) =>
            prev.map((x) => (x.id === row.id ? { ...x, envio_status: 'OK', envio_error: undefined } : x))
        )
        }, 900)
    }

    const enviarCorreos = async () => {
        if (!header?.periodo_id || !header?.anio || !header?.num_periodo) {
        message.warning('Selecciona un periodo válido')
        return
        }
    message.loading({ key: 'send', content: 'Enviando correos…', duration: 0 })
      try {
      const response = await enviarRecibosPorCorreo(header.periodo_id, header.anio, header.num_periodo)

      // Si el payload está vacío o es null/undefined, mostrar advertencia
      if (!response || response.payload === null || response.payload === undefined || response.payload === '') {
        message.warning({ key: 'send', content: response?.message || 'No se encontraron empleados válidos para enviar', duration: 3 })
        return
      }

      if (response?.ok) {
        message.success({ key: 'send', content: 'Correos enviados', duration: 1.5 })
        await refreshData(header)
      } else {
        message.error({ key: 'send', content: response?.message || 'Error al enviar correos', duration: 2 })
      }
      } catch (error) {
      message.error({ key: 'send', content: 'Error al enviar correos', duration: 2 })
      }
    }

    // ====== Handlers de <AnioPeriodo /> ======
    const onBlurHeader = async (values, dataPeriod, statusInfo) => {
        setHeader(values)
        setPeriodData(dataPeriod)
        setStatus(statusInfo?.value)
        await Promise.all([
        cargarResumen(values),
        refreshData(values),
        ]);
    }

  const getPeriodDetails = async (details) => setPeriodData(details)

  return (
    <PageApp routes={routes} title="Enviar Recibos por Correo">
        <AnioPeriodo
            onBlur={onBlurHeader}
            getPeriodDetails={getPeriodDetails}
            form={form}
            disabledForm={false}
        />
        <Divider />

        {/* Estadísticas de la bitácora (igual que en TimbrarNomina) */}
        {bitacoraInfo && (
            <Row gutter={16} style={{ paddingTop: 10, paddingBottom: 10 }}>
            <Col span={7} offset={1}>
                <Statistic
                title="Timbrados"
                value={bitacoraInfo.usuarios_procesados}
                suffix={`/ ${bitacoraInfo.usuarios_esperados}`}
                />
            </Col>
            <Col span={7}>
                <Statistic
                title="Enviados"
                value={bitacoraInfo.usuarios_enviados}
                suffix={`/ ${bitacoraInfo.usuarios_esperados}`}
                prefix={<CheckOutlined style={{ color: 'green' }} />}
                />
            </Col>
            <Col span={7}>
                <Statistic
                title="Incorrectos"
                value={bitacoraInfo.usuarios_procesados_error}
                suffix={`/ ${bitacoraInfo.usuarios_esperados}`}
                prefix={<CloseOutlined style={{ color: 'red' }} />}
                />
            </Col>
            </Row>
        )}
        <Divider />

        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col>
            <Space>
              <Button type="primary" icon={<MailOutlined />} size="large" onClick={enviarCorreos}>
                ENVIAR CORREOS
              </Button>
              <Tooltip title="Enviar correo a todos los empleados con nómina procesada">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          </Col>
          <Col>
            <Select
              placeholder="Filtrar por estatus"
              allowClear
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 200 }}
              options={[
                { label: 'Todos', value: null },
                { label: 'En proceso', value: status_recibo_nomina.EN_PROCESO },
                { label: 'Procesado', value: status_recibo_nomina.PROCESADO },
                { label: 'Cerrado', value: status_recibo_nomina.CERRADO },
                { label: 'Timbrado', value: status_recibo_nomina.TIMBRADO },
                { label: 'Enviado', value: status_recibo_nomina.ENVIADO },
                { label: 'Error', value: status_recibo_nomina.ERROR },
              ]}
            />
          </Col>
        </Row>

      {/* Tabla de empleados y estatus correo */}
      <Card style={{ marginTop: 16 }}>
        {filteredItems.length === 0 ? (
          <Empty />
        ) : (
          <Table
            rowKey={(r) => r.id}
            dataSource={filteredItems}
            columns={columns}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>
    </PageApp>
  )
}

export default EnviarRecibosEmailPage

// ====== Componentes auxiliares ======
function KPI({ titulo, valor, icono }) {
  return (
    <Card bodyStyle={{ padding: 16 }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text type="secondary">{titulo}</Text>
        <Row align="middle" gutter={8}>
          {icono && (
            <Col>
              <span style={{ fontSize: 22, lineHeight: 1 }}>{icono}</span>
            </Col>
          )}
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              {valor}
            </Title>
          </Col>
        </Row>
      </Space>
    </Card>
  )
}
